import type { TFunction } from 'i18next';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import type { AsyncComboboxOption } from '@/shared/components/custom/async-combobox/AsyncCombobox.types';
import { useUpdateMember } from '../../hooks/useUpdateMember';
import { useToast } from '@/shared/hooks/useToast';
import { MEMBER_ROLES, MemberRole, type Member } from '../../types';

interface RoleCellProps {
  member: Member;
  t: TFunction;
}

const ROLE_COLORS: Record<MemberRole, { bg: string; text: string }> = {
  [MemberRole.Owner]: { bg: 'hsl(var(--primary))', text: 'hsl(var(--primary-foreground))' },
  [MemberRole.Admin]: { bg: 'hsl(var(--secondary))', text: 'hsl(var(--secondary-foreground))' },
  [MemberRole.Collaborator]: { bg: 'hsl(var(--muted))', text: 'hsl(var(--muted-foreground))' },
  [MemberRole.Client]: { bg: 'hsl(var(--muted))', text: 'hsl(var(--muted-foreground))' },
};

function renderRoleBadge(option: AsyncComboboxOption) {
  const colors = ROLE_COLORS[option.id as MemberRole] ?? ROLE_COLORS[MemberRole.Collaborator];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ background: colors.bg, color: colors.text }}
    >
      {option.label}
    </span>
  );
}

const EDITABLE_ROLES = MEMBER_ROLES.filter((r) => r !== MemberRole.Owner);

export function RoleCell({ member, t }: RoleCellProps) {
  const { mutate: updateMember, isPending } = useUpdateMember();
  const { toast } = useToast();

  const isEditable = member.role !== MemberRole.Owner && !member.isCurrentUser;

  const staticOptions: AsyncComboboxOption[] = EDITABLE_ROLES.map((role) => ({
    id: role,
    label: t(`role.${role}`),
  }));

  const currentOption: AsyncComboboxOption = {
    id: member.role,
    label: t(`role.${member.role}`),
  };

  const handleChange = (roleId: string | null) => {
    if (!roleId || roleId === member.role) return;
    updateMember(
      { memberId: member.id, payload: { role: roleId } },
      {
        onSuccess: () =>
          toast.success({
            title: t('toast.roleUpdated'),
            description: t('toast.roleUpdatedDesc', { name: member.displayName }),
          }),
      },
    );
  };

  return (
    <div data-testid={`members-role-cell-${member.id}`}>
      <AsyncCombobox
        mode="single"
        queryKey={['role-cell', member.id]}
        staticOptions={staticOptions}
        value={member.role}
        onChange={handleChange}
        clearable={false}
        disabled={!isEditable || isPending}
        selectedOptions={[currentOption]}
        renderValue={renderRoleBadge}
        triggerClassName="w-auto border-0 shadow-none bg-transparent hover:bg-transparent p-0 h-auto ring-0 focus-visible:ring-0 focus-visible:border-0"
      />
    </div>
  );
}
